import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { formatRelativeTime, formatFullDate } from '@/shared/utils/dateUtils';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import {
    getReplies,
    createReply,
    deleteReply,
    likeReply,
    dislikeReply,
    checkReplyEngagement,
} from '@/shared/services/replyApi';

import ReplyCard from './ReplyCard';
import ReplyInput from './ReplyInput';
import ReplyReportModal from './ReplyReportModal';

const MAX_VISUAL_DEPTH = 5; // Stop visual nesting at depth 5

const ReplySection = ({ postId, isModerator = false, onReplyCountChange }) => {
    const { user, isAdmin } = useAuth();
    const axiosInstance = useAuthenticatedAxios();

    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeReplyTo, setActiveReplyTo] = useState(null);
    const [replyEngagements, setReplyEngagements] = useState({});
    const [collapsedReplies, setCollapsedReplies] = useState(new Set());
    const [replyDrafts, setReplyDrafts] = useState({});
    const [flashMessage, setFlashMessage] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null); // { replyId, message }
    // Generic discard-draft confirmation. Holds the message shown in the modal
    // and the action to run on confirm, so the switch/cancel flows can use an
    // accessible in-app modal instead of window.confirm.
    const [discardConfirm, setDiscardConfirm] = useState(null); // { message, onConfirm }
    const [reportingReplyId, setReportingReplyId] = useState(null); // Reply ID currently being reported
    const isInitialLoad = useRef(true);
    const knownParentIds = useRef(new Set());

    // Fetch all replies for the post
    const fetchReplies = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getReplies({ postId });
            setReplies(data);
            setError(null);

            // Auto-collapse logic: on initial load collapse all parents.
            // On refetches (e.g. after posting a reply), only auto-collapse
            // newly-appeared parents so the user's expand/collapse state is
            // preserved.
            const repliesWithChildren = new Set();
            data.forEach((reply) => {
                const hasChildren = data.some((r) => r.parentId === reply._id);
                if (hasChildren) {
                    repliesWithChildren.add(reply._id);
                }
            });
            if (isInitialLoad.current) {
                setCollapsedReplies(repliesWithChildren);
                isInitialLoad.current = false;
            } else {
                setCollapsedReplies((prev) => {
                    const next = new Set(prev);
                    for (const id of repliesWithChildren) {
                        // Only auto-collapse parents that are brand new
                        // (not previously seen as a parent).
                        if (!knownParentIds.current.has(id)) {
                            next.add(id);
                        }
                    }
                    return next;
                });
            }
            // Track which reply IDs have been seen as parents.
            for (const id of repliesWithChildren) {
                knownParentIds.current.add(id);
            }

            //  fetch engagements (auth-protected — skip for anonymous users)
            if (user) {
                const results = await Promise.all(
                    data.map((reply) =>
                        checkReplyEngagement(axiosInstance, reply._id).then(
                            (engagementData) => ({
                                replyId: reply._id,
                                engagementData,
                            })
                        )
                    )
                );
                const engagements = results.reduce(
                    (acc, { replyId, engagementData }) => {
                        acc[replyId] = engagementData;
                        return acc;
                    },
                    {}
                );
                setReplyEngagements(engagements);
            } else {
                setReplyEngagements({});
            }

            if (onReplyCountChange) {
                onReplyCountChange(data.length);
            }
        } catch (err) {
            console.error('Error fetching replies:', err);
            setError('Failed to load replies');
        } finally {
            setLoading(false);
        }
    }, [postId, axiosInstance, onReplyCountChange, user]);

    useEffect(() => {
        fetchReplies();
    }, [fetchReplies]);

    // Handle creating a new reply
    const handleCreateReply = useCallback(
        async (replyData) => {
            if (!user) {
                setFlashMessage({
                    variant: 'warning',
                    text: 'Please log in to reply.',
                });
                return;
            }

            try {
                const result = await createReply(axiosInstance, replyData);

                if (result?.moderationStatus === 'blocked') {
                    // Keep the reply input open so the user can edit — throw so
                    // ReplyInput displays the message inline and preserves content
                    throw new Error(
                        'Your reply was blocked because it violates our community guidelines. Please edit your content and try again.'
                    );
                }

                if (result?.moderationStatus === 'pending_review') {
                    setFlashMessage({
                        variant: 'info',
                        text: 'Your reply is pending review. It will be posted once a moderator has approved it.',
                    });
                    setReplyDrafts((prev) => {
                        const updated = { ...prev };
                        if (replyData.parentId) {
                            delete updated[replyData.parentId];
                        } else {
                            delete updated['top-level'];
                        }
                        return updated;
                    });
                    setActiveReplyTo(null);
                    return;
                }

                // Published — expand parent if collapsed, clear draft, refresh.
                // ReplyInput's onSubmit payload carries the parent id under the
                // key `parentId` (see ReplyInput.handleSubmit), so we branch on
                // that here. Reading `parentData.parentReplyId` would always be
                // undefined and the collapsed parent would never re-expand.
                if (replyData.parentId) {
                    setCollapsedReplies((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(replyData.parentId);
                        return newSet;
                    });
                }

                setReplyDrafts((prev) => {
                    const updated = { ...prev };
                    if (replyData.parentId) {
                        delete updated[replyData.parentId];
                    } else {
                        delete updated['top-level'];
                    }
                    return updated;
                });

                await fetchReplies();
                setActiveReplyTo(null);
            } catch (err) {
                console.error('Error creating reply:', err);

                // Re-throw blocked moderation errors so ReplyInput shows them inline
                if (
                    err.message?.includes('violates our community guidelines')
                ) {
                    throw err;
                }

                const backendMessage = err.response?.data?.message;
                setFlashMessage({
                    variant: 'danger',
                    text:
                        backendMessage ||
                        'Failed to post reply. Please try again.',
                });
            }
        },
        [user, axiosInstance, fetchReplies]
    );

    // Handle deleting a reply -- opens confirm modal instead of window.confirm
    const handleDeleteReply = useCallback((replyId) => {
        setConfirmDelete({
            replyId,
            message:
                'Are you sure you want to delete this reply? Nested replies underneath it will stay live.',
        });
    }, []);

    const executeDeleteReply = useCallback(async () => {
        if (!confirmDelete) return;
        const { replyId } = confirmDelete;
        setConfirmDelete(null);

        try {
            await deleteReply(axiosInstance, replyId);
            // Refetch so the tombstoned reply re-renders in place with the
            // "[deleted by ...]" placeholder. The server's index endpoint
            // now returns published + deleted replies for thread-shape
            // preservation.
            await fetchReplies();
        } catch (err) {
            console.error('Error deleting reply:', err);
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to delete reply. Please try again.',
            });
        }
    }, [confirmDelete, axiosInstance, fetchReplies]);

    // Handle reporting a reply
    const handleReportReply = useCallback((replyId) => {
        setReportingReplyId(replyId);
    }, []);

    // Handle switching reply boxes with draft warning
    const handleSwitchReplyTo = useCallback(
        (newReplyId) => {
            // Clear the current draft and switch to the new reply box.
            const performSwitch = () => {
                setReplyDrafts((prev) => {
                    const updated = { ...prev };
                    if (activeReplyTo) {
                        delete updated[activeReplyTo];
                    } else {
                        delete updated['top-level'];
                    }
                    return updated;
                });
                setActiveReplyTo(newReplyId);
            };

            // Check if there's an unsaved draft in the current reply box
            const currentDraft = activeReplyTo
                ? replyDrafts[activeReplyTo]
                : replyDrafts['top-level'];

            // Only show warning if there's actual content (more than just whitespace)
            const hasContent = currentDraft && currentDraft.trim().length > 0;

            if (activeReplyTo !== newReplyId && hasContent) {
                setDiscardConfirm({
                    message:
                        'You have an unsaved reply. Discard and switch to a different reply?',
                    onConfirm: performSwitch,
                });
                return;
            }

            performSwitch();
        },
        [activeReplyTo, replyDrafts]
    );

    // Handle cancel with draft warning
    const handleCancelReply = useCallback(
        (replyId) => {
            // Clear the draft and close the reply box.
            const performCancel = () => {
                setReplyDrafts((prev) => {
                    const updated = { ...prev };
                    if (replyId) {
                        delete updated[replyId];
                    } else {
                        delete updated['top-level'];
                    }
                    return updated;
                });
                setActiveReplyTo(null);
            };

            const currentDraft = replyId
                ? replyDrafts[replyId]
                : replyDrafts['top-level'];

            // Only show warning if there's actual content (more than just whitespace)
            const hasContent = currentDraft && currentDraft.trim().length > 0;

            if (hasContent) {
                setDiscardConfirm({
                    message: 'You have an unsaved reply. Discard and close?',
                    onConfirm: performCancel,
                });
                return;
            }

            performCancel();
        },
        [replyDrafts]
    );

    // Toggle collapse state for a reply
    const toggleCollapseReply = useCallback((replyId) => {
        setCollapsedReplies((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(replyId)) {
                newSet.delete(replyId);
            } else {
                newSet.add(replyId);
            }
            return newSet;
        });
    }, []);

    // Handle like/unlike reply
    const handleLikeReply = useCallback(
        async (replyId) => {
            if (!user) {
                setFlashMessage({
                    variant: 'warning',
                    text: 'Please log in to like replies.',
                });
                return;
            }

            try {
                const data = await likeReply(axiosInstance, replyId);

                const newReaction = data.reaction;
                const likeCount = data.likeCount;
                const dislikeCount = data.dislikeCount;

                // Update local state based on server response
                setReplyEngagements((prev) => ({
                    ...prev,
                    [replyId]: { ...prev[replyId], reaction: newReaction },
                }));

                setReplies((prevReplies) =>
                    prevReplies.map((reply) =>
                        reply._id === replyId
                            ? { ...reply, likeCount, dislikeCount }
                            : reply
                    )
                );
            } catch (err) {
                console.error('Error updating like:', err);
            }
        },
        [user, axiosInstance]
    );

    // Handle dislike / undislike reply
    const handleDislikeReply = useCallback(
        async (replyId) => {
            if (!user) {
                setFlashMessage({
                    variant: 'warning',
                    text: 'Please log in to dislike replies.',
                });
                return;
            }

            try {
                const data = await dislikeReply(axiosInstance, replyId);

                const newReaction = data.reaction;
                const dislikeCount = data.dislikeCount;
                const likeCount = data.likeCount;

                // Update local state based on server response
                setReplyEngagements((prev) => ({
                    ...prev,
                    [replyId]: { ...prev[replyId], reaction: newReaction },
                }));

                setReplies((prevReplies) =>
                    prevReplies.map((reply) =>
                        reply._id === replyId
                            ? { ...reply, likeCount, dislikeCount }
                            : reply
                    )
                );
            } catch (err) {
                console.error('Error updating dislike:', err);
            }
        },
        [user, axiosInstance]
    );

    // Build proper reply tree with parent-child relationships
    const buildReplyTree = useCallback(() => {
        const topLevel = [];
        const childrenMap = new Map();
        const replyMap = new Map();

        // Build reply map for quick lookups
        replies.forEach((reply) => {
            replyMap.set(reply._id, reply);
            childrenMap.set(reply._id, []);
        });

        // Cycle detection: check if a reply creates a cycle
        const detectCycle = (replyId, parentId) => {
            const visited = new Set();
            let currentId = parentId;

            while (currentId) {
                if (currentId === replyId) {
                    return true; // Cycle detected
                }
                if (visited.has(currentId)) {
                    return true; // Already visited - cycle
                }
                visited.add(currentId);

                const parent = replyMap.get(currentId);
                if (!parent) break;
                currentId = parent.parentId;
            }

            return false;
        };

        // Group replies by their DIRECT parent (not root parent)
        replies.forEach((reply) => {
            if (!reply.parentId) {
                // Top-level reply
                topLevel.push(reply);
            } else {
                // Check for cycles
                if (detectCycle(reply._id, reply.parentId)) {
                    console.error(
                        `Cycle detected for reply ${reply._id}. Treating as top-level.`
                    );
                    topLevel.push(reply);
                    return;
                }

                // Child reply - add to direct parent's children
                if (childrenMap.has(reply.parentId)) {
                    childrenMap.get(reply.parentId).push(reply);
                } else {
                    // Parent doesn't exist (orphaned) - add as top-level
                    console.error(
                        `Orphaned reply ${reply._id} (parent ${reply.parentId} not found). Treating as top-level.`
                    );
                    topLevel.push(reply);
                }
            }
        });

        return { topLevel, childrenMap };
    }, [replies]);

    // Recursively render a reply and all its children
    const renderReply = useCallback(
        (reply, childrenMap, depth = 0) => {
            // Safety limit to prevent UI crashes (matches backend limit)
            if (depth > 100) {
                console.error(
                    `Maximum render depth exceeded for reply ${reply._id}`
                );
                return null;
            }

            const parentReply = reply.parentId
                ? replies.find((r) => r._id === reply.parentId)
                : null;

            const children = childrenMap.get(reply._id) || [];
            const isCollapsed = collapsedReplies.has(reply._id);
            const hasChildren = children.length > 0;

            // Cap visual depth: replies at depth 5+ all render at indent 5
            const visualDepth = Math.min(depth, MAX_VISUAL_DEPTH);

            return (
                <div
                    key={reply._id}
                    className="reply-thread-item"
                    data-depth={visualDepth}
                >
                    <ReplyCard
                        replyId={reply._id}
                        userName={reply.authorName}
                        authorUsername={reply.authorName}
                        userProfilePic={reply.authorProfilePic}
                        userDate={formatRelativeTime(reply.createdAt)}
                        userFullDate={formatFullDate(reply.createdAt)}
                        content={reply.content}
                        parentUserName={
                            parentReply ? parentReply.authorName : null
                        }
                        likes={reply.likeCount}
                        dislikes={reply.dislikeCount}
                        replyCount={children.length}
                        reaction={replyEngagements[reply._id]?.reaction || null}
                        depth={visualDepth}
                        status={reply.status}
                        deletedByRole={reply.deletedByRole}
                        isCollapsed={isCollapsed}
                        hasChildren={hasChildren}
                        onToggleCollapse={() => toggleCollapseReply(reply._id)}
                        onLike={() => handleLikeReply(reply._id)}
                        onDislike={() => handleDislikeReply(reply._id)}
                        onReply={() => handleSwitchReplyTo(reply._id)}
                        onReport={() => handleReportReply(reply._id)}
                        onDelete={() => handleDeleteReply(reply._id)}
                        canDelete={
                            user && (reply.isMine || isModerator || isAdmin)
                        }
                        user={user}
                    />

                    {/* Show nested reply input */}
                    {activeReplyTo === reply._id && user && (
                        <div
                            className="reply-input-nested"
                            style={{ '--reply-indent': visualDepth + 1 }}
                        >
                            <ReplyInput
                                postId={postId}
                                parentReplyId={reply._id}
                                parentUserName={reply.authorName}
                                userProfilePic={user.profilePicture}
                                onSubmit={handleCreateReply}
                                onCancel={() => handleCancelReply(reply._id)}
                                onContentChange={(content) =>
                                    setReplyDrafts((prev) => ({
                                        ...prev,
                                        [reply._id]: content,
                                    }))
                                }
                            />
                        </div>
                    )}

                    {/* Recursively render child replies when expanded */}
                    {hasChildren &&
                        !isCollapsed &&
                        children.map((childReply) =>
                            renderReply(childReply, childrenMap, depth + 1)
                        )}
                </div>
            );
        },
        [
            replies,
            replyEngagements,
            user,
            isModerator,
            isAdmin,
            activeReplyTo,
            postId,
            collapsedReplies,
            handleLikeReply,
            handleSwitchReplyTo,
            handleCancelReply,
            handleDislikeReply,
            handleReportReply,
            handleDeleteReply,
            handleCreateReply,
            toggleCollapseReply,
        ]
    );

    if (loading) {
        return (
            <div className="reply-section-loading text-center py-4">
                <Spinner animation="border" variant="primary" />
                <span className="visually-hidden">Loading...</span>
                <p className="text-muted mt-2">Loading replies...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="my-3">
                {error}
            </Alert>
        );
    }

    const { topLevel, childrenMap } = buildReplyTree();

    return (
        <div className="reply-section">
            {/* Flash message (replaces window.alert) */}
            {flashMessage && (
                <Alert
                    variant={flashMessage.variant}
                    dismissible
                    onClose={() => setFlashMessage(null)}
                    className="mb-3"
                >
                    {flashMessage.text}
                </Alert>
            )}

            {/* Confirm Delete Reply Modal (replaces window.confirm) */}
            <Modal
                show={!!confirmDelete}
                onHide={() => setConfirmDelete(null)}
                centered
                aria-labelledby="confirm-delete-reply-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="confirm-delete-reply-title">
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmDelete?.message}</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setConfirmDelete(null)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={executeDeleteReply}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Discard Draft Modal (replaces window.confirm for switch/cancel) */}
            <Modal
                show={!!discardConfirm}
                onHide={() => setDiscardConfirm(null)}
                centered
                aria-labelledby="discard-reply-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="discard-reply-title">
                        Discard reply?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>{discardConfirm?.message}</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setDiscardConfirm(null)}
                    >
                        Keep editing
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            discardConfirm?.onConfirm?.();
                            setDiscardConfirm(null);
                        }}
                    >
                        Discard
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Report Reply Modal */}
            <ReplyReportModal
                show={reportingReplyId !== null}
                onHide={() => setReportingReplyId(null)}
                replyId={reportingReplyId}
                axiosInstance={axiosInstance}
                onReportSuccess={() => {}}
            />

            <div className="reply-section-header mb-3">
                <h6 className="mb-0">Replies ({replies.length})</h6>
            </div>

            {/* Top-level reply input - only show when no nested reply is active */}
            {user && !activeReplyTo && (
                <ReplyInput
                    postId={postId}
                    userProfilePic={user.profilePicture}
                    onSubmit={handleCreateReply}
                    placeholder="Write a reply..."
                    onContentChange={(content) =>
                        setReplyDrafts((prev) => ({
                            ...prev,
                            'top-level': content,
                        }))
                    }
                />
            )}

            {!user && (
                <p className="text-muted small mb-3">
                    Please log in to reply to this post.
                </p>
            )}

            {/* Reply thread */}
            <div className="reply-thread">
                {topLevel.length > 0 ? (
                    topLevel.map((reply) => renderReply(reply, childrenMap, 0))
                ) : (
                    <p className="text-muted text-center py-4">
                        No replies yet. Be the first to reply!
                    </p>
                )}
            </div>
        </div>
    );
};

ReplySection.propTypes = {
    postId: PropTypes.string.isRequired,
    isModerator: PropTypes.bool,
    onReplyCountChange: PropTypes.func,
};

export default ReplySection;
